import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Observable, first } from 'rxjs';
import { RokezzzAuthorizationService } from 'src/app/rokezzz/user/services/authorization.service';
import { ApiCleanResponse } from '../api/api-clean-response';
import { ApiFilterConfig } from '../api/api-filter-config';
import { ApiOptions } from '../api/api-options';
import { ApiResponse } from '../api/api-response';
import { OnFinish } from '../api/on-finish';

@Injectable({
    providedIn: 'root'
})
export class RokezzzApiService {
    public readonly apiUrl: string = '/api/';

    private recaptchaToken?: string;
    private contentType?: string = 'application/json';

    public constructor(
        private readonly http: HttpClient,
        private readonly rokezzzAuthorizationService: RokezzzAuthorizationService,
        @Inject(LOCALE_ID) private readonly localeId: string
    ) { }

    public setRecaptchaToken(token: string): void {
        this.recaptchaToken = token;
    }

    public setMultipartContentType(): void {
        this.contentType = undefined;
    }

    public prepareGet(endpoint: string, data: Record<string, string>): Observable<Object> {
        return this.http
            .get(this.apiUrl + endpoint + this.getUriParams(data), this.getOptions());
    }

    public prepareGetCollection(
        endpoint: string,
        data: Record<string, string>,
        apiFilterConfig: ApiFilterConfig
    ): Observable<ApiCleanResponse<Object>> {
        data['page'] = apiFilterConfig.page;
        data['itemsPerPage'] = apiFilterConfig.itemsPerPage;
        if (apiFilterConfig.filter) {
            data['filter'] = apiFilterConfig.filter;
        }

        if (apiFilterConfig.sortField) {
            data[`order[${apiFilterConfig.sortField}]`] = apiFilterConfig.sortDirection ?? 'asc';
        }

        return this.http.get(
            this.apiUrl + endpoint + this.getUriParams(data),
            this.getOptions('application/ld+json')
        ) as Observable<ApiCleanResponse<Object>>;
    }

    public preparePost(endpoint: string, data: object): Observable<Object> {
        return this.http
            .post(this.apiUrl + endpoint, data, this.getOptions());
    }

    public preparePostWithParam(endpoint: string, params: Record<string, string>, data: object): Observable<Object> {
        return this.http
            .post(this.apiUrl + endpoint + this.getUriParams(params), data, this.getOptions());
    }

    public preparePatch(endpoint: string, data: object): Observable<Object> {
        return this.http
            .patch(this.apiUrl + endpoint, data, this.getOptions());
    }

    public preparePatchWithParam(endpoint: string, param: Record<string, string>, data: object): Observable<Object> {
        return this.http
            .patch(this.apiUrl + endpoint + this.getUriParams(param), data, this.getOptions());
    }

    public preparePut(endpoint: string, data: object): Observable<Object> {
        return this.http
            .put(this.apiUrl + endpoint, data, this.getOptions());
    }

    public prepareDelete(endpoint: string, data: Record<string, string>): Observable<Object> {
        return this.http
            .delete(this.apiUrl + endpoint + this.getUriParams(data), this.getOptions());
    }

    public get(
        endpoint: string,
        data: Record<string, string>,
        onSuccess: OnFinish<any>,
        onError: OnFinish<HttpErrorResponse>
    ): void {
        this.prepareGet(endpoint, data).pipe(
            first()
        ).subscribe({
            next: onSuccess,
            error: onError
        });
    }

    public getCollection(
        endpoint: string,
        data: Record<string, string>,
        apiFilterConfig: ApiFilterConfig,
        onSuccess: OnFinish<ApiResponse<any>>,
        onError: OnFinish<HttpErrorResponse>
    ): void {
        this.prepareGetCollection(endpoint, data, apiFilterConfig).pipe(
            first()
        ).subscribe({
            next: (apiCleanResponse: ApiCleanResponse<any>) => {
                const total: number = apiCleanResponse['hydra:totalItems'],
                    apiResponse: ApiResponse<any> = {
                        data: apiCleanResponse['hydra:member'],
                        totalItems: total,
                        totalPages: Math.ceil(total / parseInt(apiFilterConfig.itemsPerPage))
                    };

                onSuccess(apiResponse);
            },
            error: onError
        });
    }

    public post(
        endpoint: string,
        data: object,
        onSuccess: OnFinish<any>,
        onError: OnFinish<HttpErrorResponse>
    ): void {
        this.preparePost(endpoint, data).pipe(
            first()
        ).subscribe({
            next: onSuccess,
            error: onError
        });
    }

    public postWithParam(
        endpoint: string,
        param: Record<string, string>,
        data: object,
        onSuccess: OnFinish<any>,
        onError: OnFinish<HttpErrorResponse>
    ): void {
        this.preparePostWithParam(endpoint, param, data).pipe(
            first()
        ).subscribe({
            next: onSuccess,
            error: onError
        });
    }

    public patch(
        endpoint: string,
        data: object,
        onSuccess: OnFinish<any>,
        onError: OnFinish<HttpErrorResponse>
    ): void {
        this.preparePatch(endpoint, data).pipe(
            first()
        ).subscribe({
            next: onSuccess,
            error: onError
        });
    }

    public patchWithParam(
        endpoint: string,
        param: Record<string, string>,
        data: object,
        onSuccess: OnFinish<any>,
        onError: OnFinish<HttpErrorResponse>
    ): void {
        this.preparePatchWithParam(endpoint, param, data).pipe(
            first()
        ).subscribe({
            next: onSuccess,
            error: onError
        });
    }

    public put(
        endpoint: string,
        data: object,
        onSuccess: OnFinish<any>,
        onError: OnFinish<HttpErrorResponse>
    ): void {
        this.preparePut(endpoint, data).pipe(
            first()
        ).subscribe({
            next: onSuccess,
            error: onError
        });
    }

    public delete(
        endpoint: string,
        data: Record<string, string>,
        onSuccess: OnFinish<any>,
        onError: OnFinish<HttpErrorResponse>
    ): void {
        this.prepareDelete(endpoint, data).pipe(
            first()
        ).subscribe({
            next: onSuccess,
            error: onError
        });
    }

    private getOptions(accept: string = 'application/json'): ApiOptions {
        let headers: HttpHeaders = new HttpHeaders()
            .set('Accept', accept)
            .set('Accept-Language', this.localeId)
            .set('Authorization', this.rokezzzAuthorizationService.getAuthorization());

        if (this.contentType) {
            headers = headers.set('Content-Type', this.contentType);
        }
        if (this.recaptchaToken) {
            headers = headers.set('Recaptcha-Token', this.recaptchaToken);
        }

        this.recaptchaToken = undefined;
        this.contentType = 'application/json';

        return {
            observe: 'body',
            responseType: 'json',
            headers: headers
        };
    }

    private getUriParams(data: Record<string, string>): string {
        const searchParams: URLSearchParams = new URLSearchParams(data);

        return '?' + searchParams.toString();
    }
}
